import React, { useCallback, useEffect, useRef, useState } from "react"
import { LexicalText, STARTING_NODES } from "@/schema"
import { Button } from "@ui/shad"
import { cn } from "@ui/utils"
import { Paperclip } from "lucide-react"

import { useFetchCurrentUser } from "@/hooks/api/users"
import { PROMPT_INPUT_GLOBAL_DROP_EXCLUDE_ATTR } from "@/components/ai-elements/prompt-input"
import { Editor, EditorRefHandle } from "@/components/lexical"
import { MentionDropdownPlugin } from "@/components/lexical/plugins/mention-dropdown"
import type { MentionOption } from "@/components/lexical/plugins/mention-dropdown/types"
import { VoiceMicButton } from "@/components/voice-transcription/voice-mic-button"

import { UserAvatar } from "../users/user-avatar"
import { COMMENT_ATTACHMENT_ACCEPT, validateCommentAttachments } from "./attachment-policy"
import { CommentAttachmentPastePlugin } from "./comment-attachment-paste-plugin"
import { PendingCommentAttachments } from "./comment-attachments"

interface CommentComposerProps {
  onSubmit: (content: LexicalText, files: File[]) => void
  onCancel?: () => void
  isSubmitting?: boolean
  placeholder?: string
  autoFocus?: boolean
  submitLabel?: string
  initialContent?: LexicalText
  showMicButton?: boolean
  resetVersion?: number
  allowMentions?: boolean
  mentionOptions?: MentionOption[]
}

export function CommentComposer({
  onSubmit,
  onCancel,
  isSubmitting = false,
  placeholder = "Add comment...",
  autoFocus = false,
  submitLabel = "Comment",
  initialContent,
  showMicButton = true,
  resetVersion = 0,
  allowMentions = true,
  mentionOptions,
}: CommentComposerProps) {
  const [content, setContent] = useState<LexicalText | null>(initialContent ?? null)
  const [files, setFiles] = useState<File[]>([])
  const { data: user } = useFetchCurrentUser()
  const editorRef = useRef<EditorRefHandle>(null)
  const isMentionMenuOpenRef = useRef(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const resetVersionRef = useRef(resetVersion)

  useEffect(() => {
    if (autoFocus) {
      setTimeout(() => editorRef.current?.focus(), 0)
    }
  }, [autoFocus])

  useEffect(() => {
    if (resetVersionRef.current === resetVersion) return

    resetVersionRef.current = resetVersion
    editorRef.current?.updateContent({ nodes: STARTING_NODES })
    setContent(null)
    setFiles([])
    setTimeout(() => {
      editorRef.current?.focus()
    }, 0)
  }, [resetVersion])

  const handleSubmit = () => {
    if (isMentionMenuOpenRef.current) return
    if ((!content?.nodes && !files.length) || isSubmitting) return

    onSubmit(content?.nodes ? content : { nodes: STARTING_NODES }, files)
  }

  const handleCancel = () => {
    setContent(null)
    setFiles([])
    if (editorRef.current) {
      editorRef.current.updateContent({ nodes: STARTING_NODES })
    }
    onCancel?.()
  }

  const hasContent = (content && !!content.nodes) || files.length > 0

  const addFiles = useCallback((selectedFiles: File[]) => {
    setFiles((currentFiles) => [...currentFiles, ...validateCommentAttachments(selectedFiles, currentFiles.length)])
  }, [])

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(event.target.files ?? []))
    event.target.value = ""
  }

  const editorStyles = {
    "--lexical-editor-font-size": "14px",
  }

  return (
    <div
      {...{ [PROMPT_INPUT_GLOBAL_DROP_EXCLUDE_ATTR]: "" }}
      className={cn("focus-within:border-primary rounded-xl border p-2", initialContent && "bg-background")}
      onDragOver={(e) => {
        // Only handle real file drags; prevent chat/global handlers from stealing the event.
        if (e.dataTransfer?.types?.includes("Files")) {
          e.preventDefault()
          e.stopPropagation()
        }
      }}
      onDrop={(e) => {
        if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
          e.preventDefault()
          e.stopPropagation()
          // Consume the drop even when disabled so chat does not steal it.
          if (!(isSubmitting || Boolean(initialContent))) {
            addFiles(Array.from(e.dataTransfer.files))
          }
        }
      }}
    >
      <div className="flex items-start gap-1">
        <UserAvatar userId={user?.id} size="xs" className="mt-[3px]" />
        <div className="w-full">
          <Editor
            ref={editorRef}
            onChange={setContent}
            placeholder={placeholder}
            startingPlaceholder={placeholder}
            initialContent={initialContent}
            editable={!isSubmitting}
            disableHeadings={true}
            disableSlashCommands={true}
            styleOverrides={editorStyles}
            shouldIgnoreEnter={() => isMentionMenuOpenRef.current}
            onEnter={handleSubmit}
          >
            <CommentAttachmentPastePlugin onPasteImages={addFiles} disabled={isSubmitting || Boolean(initialContent)} />
            {allowMentions && (
              <MentionDropdownPlugin
                mentionableTypes={["user"]}
                options={mentionOptions}
                onOpenChange={(open) => (isMentionMenuOpenRef.current = open)}
              />
            )}
          </Editor>
          <PendingCommentAttachments
            files={files}
            onRemove={(index) => setFiles((currentFiles) => currentFiles.filter((_, fileIndex) => fileIndex !== index))}
            disabled={isSubmitting}
          />
        </div>
      </div>

      <div className="mt-2 flex items-center justify-end gap-2">
        {!initialContent && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={COMMENT_ATTACHMENT_ACCEPT}
              className="sr-only"
              onChange={handleFileChange}
              disabled={isSubmitting}
              aria-label="Choose comment attachments"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={isSubmitting}
            >
              <Paperclip />
              Attach
            </Button>
          </>
        )}
        {onCancel && (
          <Button variant="ghost" size="sm" onClick={handleCancel} disabled={isSubmitting}>
            Cancel
          </Button>
        )}

        {showMicButton && <VoiceMicButton editorRef={editorRef} disabled={isSubmitting} />}
        <Button size="sm" onClick={handleSubmit} disabled={!hasContent || isSubmitting}>
          {isSubmitting ? "Sending..." : submitLabel}
        </Button>
      </div>
    </div>
  )
}
